import { useState, useEffect } from "react";
import { string, shape, array, object, func } from "prop-types";
import ToolbarTooltip from "./ToolbarTooltip/ToolbarTooltip.jsx";
import cx from "classnames";
import useClickAway from "../../common/useClickAway.js";
import ShareBox from "./ShareBox/ShareBox.jsx";
import showFile from "../../common/showFile.js";
// import { getPdfStringFromUrl } from "../../common/constructPdfString.js";

const propTypes = {
  pageInfo: shape({
    pageTitle: string,
    pageBreadcrumb: array,
    pageGuid: string,
  }).isRequired,
  toolbarDictionary: object,
  toolbarOptions: object,
  toolbarPdf: shape({
    title: string,
    url: string,
  }),
  setDraftReport: func,
};

const defaultProps = {
  toolbarDictionary: {
    addToReport: "Add to report",
    sectionSavedtoReport: "Section saved to report!",
    downloadPDF: "Download PDF",
    askanExpert: "Ask an Expert",
  },
  toolbarOptions: {
    addToReport: true,
    downloadPdf: true,
    share: true,
    askAnExpert: true,
  },
};

const Toolbar = (props) => {
  const {
    pageInfo,
    toolbarDictionary,
    toolbarOptions,
    toolbarPdf,
    setDraftReport,
  } = props;

  const [addPageToDraft, setAddPageToDraft] = useState(false);
  const [removePageFromDraft, setRemovePageFromDraft] = useState(false);
  const [displayUndoAddDraft, setDisplayUndoAddDraft] = useState(false);
  const [displayShare, setDisplayShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloadPage, setDownloadPage] = useState(false);
  const [didAutoDownload, setDidAutoDownload] = useState(false);

  const removeDraftRef = useClickAway({
    isOpen: displayUndoAddDraft,
    setOpen: setDisplayUndoAddDraft,
  });

  const shareRef = useClickAway({
    isOpen: displayShare,
    setOpen: shareClickAway,
  });

  window.dataLayer = window.dataLayer || [];

  //reset the copy bool and close the share bar on click away
  function shareClickAway(bool) {
    setDisplayShare(bool);
    setCopied(bool);
  }

  useEffect(() => {
    let params = new URLSearchParams(window.location.search);
    const requestedPdfUrl = params.get("requestedPdfUrl");
    const requestedPdfTitle = params.get("requestedPdfTitle");
    if (
      params.get("downloadPdf") === "true" &&
      !didAutoDownload &&
      (requestedPdfUrl || (toolbarPdf && toolbarPdf.url))
    ) {
      // the user logged in in order to download a PDF
      setDidAutoDownload(true);
      params.delete("downloadPdf");
      params.delete("requestedPdfUrl");
      params.delete("requestedPdfTitle");
      let newQuery = window.location.pathname + "?" + params.toString();
      window.history.pushState(null, "", newQuery);

      // download the pdf without opening a new tab - same as what the toolbar download pdf button currently does
      if (requestedPdfUrl) {
        downloadPdf(requestedPdfUrl, requestedPdfTitle || document.title);
      } else {
        downloadPdf(toolbarPdf.url, toolbarPdf.title || document.title);
      }
    }
  }, [didAutoDownload, toolbarPdf, toolbarPdf.url]);

  useEffect(() => {
    if (downloadPage && toolbarPdf && toolbarPdf.url) {
      setDownloadPage(false);

      // getPdfStringFromUrl(encodeURI(window.location.href), 0, false)
      //   .then(string => {
      //     const url = "/api/ReportBuilder/StreamPdf";
      //     const data = JSON.stringify({
      //       Content: string,
      //     });

      downloadPdf(toolbarPdf.url, toolbarPdf.title || document.title);
    }
  }, [downloadPage, toolbarPdf]);

  function downloadPdf(url, filename) {
    fetch(url, {
      method: "GET", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      headers: {
        "Content-Type": "application/json",
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *client
      // body: data, // body data type must match "Content-Type" header
    })
      .then((r) => r.blob())
      .then((blob) => {
        showFile(blob, filename);
      })
      .catch((e) => console.error("Failed to download PDF", e));
  }

  //add page to draft network request
  useEffect(() => {
    if (addPageToDraft) {
      setAddPageToDraft(false);

      const url = "/api/sitecore/ReportBuilder/SaveSectionInDraft";
      const data = JSON.stringify({
        sectionId: pageInfo.pageGuid,
        pageTitle: pageInfo.pageTitle,
      });

      fetch(url, {
        method: "POST", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: {
          "Content-Type": "application/json",
          // 'Content-Type': 'application/x-www-form-urlencoded',
        },
        redirect: "follow", // manual, *follow, error
        referrerPolicy: "no-referrer", // no-referrer, *client
        body: data, // body data type must match "Content-Type" header
      })
        .then((response) => {
          if (response.status === 200) {
            setDisplayUndoAddDraft(true);
            setTimeout(function () {
              setDisplayUndoAddDraft(false);
            }, 5000);
            return response.json();
          }
        })
        .then((data) => {
          setDraftReport(data.DraftReport);
        });
    }
  }, [addPageToDraft, pageInfo, setDraftReport]);

  //remove page from draft network request
  useEffect(() => {
    if (removePageFromDraft) {
      setRemovePageFromDraft(false);

      const url = "/api/sitecore/ReportBuilder/RemoveSectionFromDraft";
      const data = JSON.stringify({
        sectionId: pageInfo.pageGuid,
        pageTitle: pageInfo.pageTitle,
      });

      fetch(url, {
        method: "POST", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "same-origin", // include, *same-origin, omit
        headers: {
          "Content-Type": "application/json",
          // 'Content-Type': 'application/x-www-form-urlencoded',
        },
        redirect: "follow", // manual, *follow, error
        referrerPolicy: "no-referrer", // no-referrer, *client
        body: data, // body data type must match "Content-Type" header
      })
        .then((response) => response.json())
        .then((data) => {
          setDraftReport(data.DraftReport);
        });
    }
  }, [removePageFromDraft, pageInfo, setDraftReport]);

  function pushGTMInfo(eventName) {
    window.dataLayer.push({
      event: eventName,
    });
  }

  return (
    <div className="toolbar">
      {toolbarOptions.addToReport && (
        <div className="toolbar__item">
          <ToolbarTooltip
            triangleAlignment={0.7}
            width={105}
            top={40}
            left={27}
            content={toolbarDictionary.addToReport}
            btnIcon={
              <button
                onClick={() => {
                  pushGTMInfo("Add to Report Toolbar Click");
                  setAddPageToDraft(true);
                }}
                disabled={!toolbarOptions.addToReport}
                className="toolbar__btn"
                type="button"
                aria-label="Add to Draft"
                ref={removeDraftRef}
              >
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 48 48"
                  xmlns="http://www.w3.org/2000/svg"
                  className="toolbar__add-to-draft"
                >
                  <g fill="none" fillRule="evenodd">
                    <g transform="translate(21.23 26.692)">
                      <circle
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="currentColor"
                        cx="7.385"
                        cy="7.385"
                        r="6.385"
                      />
                      <path
                        stroke="#FFF"
                        strokeWidth=".5"
                        fill="white"
                        fillRule="nonzero"
                        d="M3.692 8.123V6.646h2.891V3.692h1.603v2.954h2.89v1.477h-2.89v2.954H6.583V8.123z"
                      />
                    </g>
                    <path
                      d="M33.23 11v14.823a9.438 9.438 0 00-2-.798V13H18.615v2.461h10.154v9.231h-.038a9.53 9.53 0 00-2.116.237v-7.314H14v16.308h5.234a9.679 9.679 0 00.154 2H12V15.615h4.615V11h16.616zM21.097 28.538a9.49 9.49 0 00-1.072 1.847h-3.409v-1.847h4.481zM24 24.846v1.846h-7.385v-1.846H24zm0-3.692V23h-7.385v-1.846H24z"
                      fill="currentColor"
                      fillRule="nonzero"
                    />
                  </g>
                </svg>
              </button>
            }
          />
          <div
            className={cx("toolbar__undo-message", {
              "toolbar__undo-message--active": displayUndoAddDraft,
            })}
          >
            <div>{toolbarDictionary.sectionSavedtoReport}</div>
            <button
              onClick={() => {
                setRemovePageFromDraft(true);
                setDisplayUndoAddDraft(false);
              }}
              className="toolbar__remove-from-draft"
              type="button"
            >
              remove
            </button>
          </div>
        </div>
      )}
      {toolbarOptions.downloadPDF && (
        <div className="toolbar__item">
          <ToolbarTooltip
            triangleAlignment={0.7}
            width={112}
            top={40}
            left={27}
            content={toolbarDictionary.downloadPDF}
            btnIcon={
              toolbarPdf && toolbarPdf.url ? (
                <button
                  aria-label="Download PDF"
                  className="toolbar__btn"
                  type="button"
                  disabled={!toolbarOptions.downloadPDF}
                  onClick={() => {
                    pushGTMInfo("Download PDF Toolbar Click");
                    setDownloadPage(true);
                  }}
                >
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    xmlns="http://www.w3.org/2000/svg"
                    className="toolbar__download-pdf"
                  >
                    <g fill="currentColor" fillRule="evenodd">
                      <path
                        d="M15.33 39v-3.012h.732c.888 0 1.59-.252 2.106-.756.516-.504.774-1.192.774-2.064 0-.864-.254-1.536-.762-2.016s-1.218-.72-2.13-.72h-2.352V39h1.632zm.66-4.44h-.66v-2.7h.66c.88 0 1.32.444 1.32 1.332 0 .912-.44 1.368-1.32 1.368zM23.374 39c1.136 0 2.022-.382 2.658-1.146.636-.764.954-1.83.954-3.198 0-1.368-.302-2.414-.906-3.138-.604-.724-1.474-1.086-2.61-1.086h-2.448V39h2.352zm.072-1.512h-.732v-5.544h.816c1.128 0 1.692.912 1.692 2.736 0 1.872-.592 2.808-1.776 2.808zM30.962 39v-3.48h2.664v-1.512h-2.664v-2.064h2.856v-1.512H29.27V39h1.692z"
                        fillRule="nonzero"
                      />
                      <path d="M31.92 25H16.08c-.595 0-1.08-.512-1.08-1.142v-4.03c0-.457.351-.828.784-.828.433 0 .784.371.784.829v3.513h14.865V19.83c0-.458.35-.829.784-.829.432 0 .783.371.783.829v4.03c0 .629-.485 1.141-1.08 1.141" />
                      <path
                        d="M29.806 14.187l-4.623 4.53a.785.785 0 01-.205.15c0 .006-.008.006-.008.006a.94.94 0 01-.941 0 1.078 1.078 0 01-.213-.148l-4.622-4.538a.637.637 0 010-.914.656.656 0 01.934 0l3.704 3.623V8.655c0-.365.303-.655.668-.655.364 0 .668.29.668.655v8.24l3.705-3.622a.655.655 0 01.933 0 .637.637 0 010 .914"
                        stroke="currentColor"
                        strokeWidth=".25"
                      />
                    </g>
                  </svg>
                </button>
              ) : (
                // We have a pdf but the user either is not signed in or doesn't have an account
                <button
                  type="button"
                  className="toolbar__btn"
                  onClick={() => {
                    window.openGlobalModal("pdfregistration");
                    return false;
                  }}
                  target="_self"
                  href="#"
                >
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    xmlns="http://www.w3.org/2000/svg"
                    className="toolbar__download-pdf"
                  >
                    <g fill="currentColor" fillRule="evenodd">
                      <path
                        d="M15.33 39v-3.012h.732c.888 0 1.59-.252 2.106-.756.516-.504.774-1.192.774-2.064 0-.864-.254-1.536-.762-2.016s-1.218-.72-2.13-.72h-2.352V39h1.632zm.66-4.44h-.66v-2.7h.66c.88 0 1.32.444 1.32 1.332 0 .912-.44 1.368-1.32 1.368zM23.374 39c1.136 0 2.022-.382 2.658-1.146.636-.764.954-1.83.954-3.198 0-1.368-.302-2.414-.906-3.138-.604-.724-1.474-1.086-2.61-1.086h-2.448V39h2.352zm.072-1.512h-.732v-5.544h.816c1.128 0 1.692.912 1.692 2.736 0 1.872-.592 2.808-1.776 2.808zM30.962 39v-3.48h2.664v-1.512h-2.664v-2.064h2.856v-1.512H29.27V39h1.692z"
                        fillRule="nonzero"
                      />
                      <path d="M31.92 25H16.08c-.595 0-1.08-.512-1.08-1.142v-4.03c0-.457.351-.828.784-.828.433 0 .784.371.784.829v3.513h14.865V19.83c0-.458.35-.829.784-.829.432 0 .783.371.783.829v4.03c0 .629-.485 1.141-1.08 1.141" />
                      <path
                        d="M29.806 14.187l-4.623 4.53a.785.785 0 01-.205.15c0 .006-.008.006-.008.006a.94.94 0 01-.941 0 1.078 1.078 0 01-.213-.148l-4.622-4.538a.637.637 0 010-.914.656.656 0 01.934 0l3.704 3.623V8.655c0-.365.303-.655.668-.655.364 0 .668.29.668.655v8.24l3.705-3.622a.655.655 0 01.933 0 .637.637 0 010 .914"
                        stroke="currentColor"
                        strokeWidth=".25"
                      />
                    </g>
                  </svg>
                </button>
              )
            }
          />
        </div>
      )}
      <div className="toolbar__item">
        <ToolbarTooltip
          triangleAlignment={0.3}
          width={112}
          top={28}
          left={20}
          content="Share Content"
          btnIcon={
            <button
              aria-label="Share Content"
              className="toolbar__btn"
              type="button"
              disabled={!toolbarOptions.share}
              onClick={() => {
                pushGTMInfo("Share Content Toolbar Click");
                setDisplayShare(!displayShare);
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                className="toolbar__share"
              >
                <path
                  d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7 0-.24-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92 0-1.61-1.31-2.92-2.92-2.92z"
                  fill="currentColor"
                  fillRule="nonzero"
                />
              </svg>
            </button>
          }
        />
        <ShareBox
          onCopy={(text, result) => setCopied(result)}
          displayShare={displayShare}
          shareRef={shareRef}
        />
      </div>
      <div className="toolbar__item">
        <ToolbarTooltip
          triangleAlignment={0.7}
          width={112}
          top={40}
          left={27}
          content={toolbarDictionary.askanExpert}
          btnIcon={
            <button
              aria-label="Ask an Expert"
              className="toolbar__btn"
              disabled={!toolbarOptions.askanExpert}
              onClick={() => {
                pushGTMInfo("Ask an Expert Toolbar Click”");
                window.openGlobalModal("askanexpert");
              }}
              type="button"
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                xmlns="http://www.w3.org/2000/svg"
                className="toolbar__ask-an-expert"
              >
                <g fillRule="nonzero" fill="none">
                  <path
                    d="M24 12a11.962 11.962 0 00-8.484 3.516A11.962 11.962 0 0012 24c0 3.312 1.344 6.312 3.516 8.484A11.962 11.962 0 0024 36c3.312 0 6.312-1.344 8.484-3.516A11.962 11.962 0 0036 24c0-3.312-1.344-6.312-3.516-8.484A11.962 11.962 0 0024 12z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <g fill="currentColor">
                    <path d="M24.98 30.744h-2.077v-2.077h2.078zM27.131 22.693l-.935.956c-.748.758-1.215 1.382-1.215 2.94h-2.078v-.52c0-1.142.467-2.181 1.215-2.94l1.289-1.309c.384-.374.613-.893.613-1.464a2.084 2.084 0 00-2.078-2.078 2.084 2.084 0 00-2.078 2.078h-2.078a4.154 4.154 0 014.156-4.156 4.154 4.154 0 014.155 4.156c0 .914-.374 1.745-.966 2.337z" />
                  </g>
                </g>
              </svg>
            </button>
          }
        />
      </div>
    </div>
  );
};

Toolbar.propTypes = propTypes;
Toolbar.defaultProps = defaultProps;
export default Toolbar;
